// Payload for the ask-user card: the multiple-choice questions a turn pauses on.
// V1 carried a single question, V2 an array; both still arrive from history.
//
// Picks are submitted back as an ordinary chat prompt of "Header: label, label"
// lines, so that text is the only link between a reply and the questions it
// answers; reading a reply back means matching the lines against the question set.

import { safeParseBlockJson } from "../message/parseBlockContent";

export interface AskUserOption {
  id: string;
  label: string;
  description?: string;
  recommended?: boolean;
}

export interface AskUserQuestion {
  question: string;
  header: string;
  multiSelect: boolean;
  options: AskUserOption[];
}

export interface AskUserData {
  type: "ask-user";
  version?: number;
  awaitingUserInput: boolean;
  questions: AskUserQuestion[];
}

const isOptionArray = (v: unknown): v is AskUserOption[] =>
  Array.isArray(v) && v.every((o) => !!o && typeof o === "object" && typeof (o as AskUserOption).label === "string");

const isQuestionArray = (v: unknown): v is AskUserQuestion[] =>
  Array.isArray(v) &&
  v.every((q) => {
    if (!q || typeof q !== "object") return false;
    const r = q as Record<string, unknown>;
    return typeof r.header === "string" && isOptionArray(r.options);
  });

/** Reads an ask-user payload out of raw block content, upgrading V1 to the V2 shape. Null for any other block. */
export function parseAskUserData(content: string | undefined | null): AskUserData | null {
  const parsed = safeParseBlockJson(content) as Record<string, unknown> | null;
  if (parsed?.type !== "ask-user") return null;

  if (isQuestionArray(parsed.questions)) {
    return {
      type: "ask-user",
      version: typeof parsed.version === "number" ? parsed.version : undefined,
      awaitingUserInput: parsed.awaitingUserInput === true,
      questions: parsed.questions,
    };
  }

  if (typeof parsed.question === "string" && isOptionArray(parsed.options)) {
    for (const [idx, opt] of parsed.options.entries()) opt.id ||= `opt${idx}`;
    return {
      type: "ask-user",
      awaitingUserInput: parsed.awaitingUserInput === true,
      questions: [
        {
          question: parsed.question,
          header: "Question",
          multiSelect: false,
          options: parsed.options,
        },
      ],
    };
  }

  return null;
}

export interface AskUserAnswer {
  questionIndex: number;
  header: string;
  optionIds: string[];
  labels: string[];
}

interface AskUserResponse {
  answers: AskUserAnswer[];
  /** Non-empty lines that matched no question header or no option label — text the user wrote rather than picked. */
  unmatchedLines: number;
}

const RECOMMENDED_SUFFIX = /\s*\(recommended\)\s*$/i;

/**
 * Display only — matching still runs against the stored label, which in chats
 * recorded before the flag existed carries the suffix.
 */
export function labelWithoutRecommendation(label: string): string {
  return label.replace(RECOMMENDED_SUFFIX, "");
}

/** The flag, or the suffix it replaced for options recorded before the flag existed. */
export function isRecommended(option: AskUserOption): boolean {
  return option.recommended === true || RECOMMENDED_SUFFIX.test(option.label);
}

// An option label can contain a comma, so a value that is a whole label wins
// over splitting it into several.
const matchOptions = (question: AskUserQuestion, value: string): AskUserOption[] | null => {
  const whole = question.options.find((opt) => opt.label === value);
  if (whole) return [whole];

  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part);
  if (parts.length === 0) return null;

  const matched: AskUserOption[] = [];
  for (const part of parts) {
    const option = question.options.find((opt) => opt.label === part);
    if (!option) return null;
    matched.push(option);
  }
  return matched;
};

/**
 * Maps the "Header: label, label" text of a reply back onto the question set it
 * answers. Callers deciding whether the text is a reply at all must require
 * `unmatchedLines` to be zero.
 */
export function parseAskUserResponse(text: string, data: AskUserData): AskUserResponse {
  const answers: AskUserAnswer[] = [];
  let unmatchedLines = 0;

  for (const line of text.split("\n")) {
    if (!line.trim()) continue;

    const colonIndex = line.indexOf(":");
    if (colonIndex === -1) {
      unmatchedLines++;
      continue;
    }

    const header = line.slice(0, colonIndex).trim();
    const questionIndex = data.questions.findIndex((q) => q.header === header);
    const question = data.questions[questionIndex];
    if (!question) {
      unmatchedLines++;
      continue;
    }

    const options = matchOptions(question, line.slice(colonIndex + 1).trim());
    if (!options) {
      unmatchedLines++;
      continue;
    }

    answers.push({
      questionIndex,
      header: question.header,
      optionIds: options.map((opt) => opt.id),
      labels: options.map((opt) => opt.label),
    });
  }

  return { answers, unmatchedLines };
}
